import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';

import { Button, ConditionView, Icon, Typography, Loader } from '@/shared/ui';

import { VerificationInput } from './VerificationInput';

import './VerificationField.scss';

export enum AvailableKeyDown {
  RIGHT = 'ArrowRight',
  LEFT = 'ArrowLeft',
  UP = 'ArrowUp',
  DOWN = 'ArrowDown',
  BACKSPACE = 'Backspace',
  DELETE = 'Delete',
}

interface VerificationFieldProps {
  label?: string;
  attemptsLeft: number;
  initialAttempts: number;
  inputQuantity: number;
  disabled?: boolean;
  validateValue: (value: string) => Promise<boolean>;
}

export const VerificationField = React.memo<VerificationFieldProps>(function ({
  label = '',
  attemptsLeft,
  initialAttempts,
  inputQuantity,
  disabled = false,
  validateValue,
}) {
  const [isVerificationInProgress, setIsVerificationInProgress] = useState<boolean>(false);
  const [isVerificationAvailable, setIsVerificationAvailable] = useState<boolean>(false);
  const [areInputsFillingByUser, setAreInputsFillingByUser] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const isFirstAttempt = useMemo<boolean>(() => attemptsLeft === initialAttempts, [attemptsLeft, initialAttempts]);
  const isErrorShown = useMemo<boolean>(
    () => !isValid && !isFirstAttempt && !areInputsFillingByUser,
    [areInputsFillingByUser, isFirstAttempt, isValid],
  );

  const getInputs = useCallback((): NodeListOf<HTMLInputElement> => {
    return document.querySelectorAll<HTMLInputElement>('.verification__field input');
  }, []);

  const getInputsValue = useCallback(
    (): string => [...getInputs()].reduce((acc, input) => (acc += input.value), ''),
    [getInputs],
  );

  const focusNextInput = useCallback(
    async (value: string) => {
      const inputs = getInputs();
      const activeInputIndex = [...inputs].findIndex(
        (input, index) => input === document.activeElement && index !== inputs.length - 1 && !!input.value,
      );
      const nextInputToFocus: Nullable<HTMLInputElement> = activeInputIndex >= 0 ? inputs[activeInputIndex + 1] : null;

      if (nextInputToFocus) {
        nextInputToFocus.focus();
      }

      if (!nextInputToFocus && !!value) {
        inputs[inputs.length - 1].blur();
      }
    },
    [getInputs],
  );

  const changeFocusByKey = useCallback(
    (key: AvailableKeyDown) => {
      const inputs = getInputs();

      let nextInputToFocus: Nullable<HTMLInputElement> = null;

      [...inputs].forEach((input, index) => {
        const isFirstItem = index === 0;
        const isLastItem = index === inputs.length - 1;

        if (input !== document.activeElement) return;

        switch (key) {
          case AvailableKeyDown.RIGHT:
            nextInputToFocus = isLastItem ? inputs[0] : inputs[index + 1];
            return;

          case AvailableKeyDown.LEFT:
            nextInputToFocus = isFirstItem ? inputs[inputs.length - 1] : inputs[index - 1];
            return;

          case AvailableKeyDown.DELETE:
            nextInputToFocus = inputs[index - 1];
            return;

          default:
            return;
        }
      });

      if (nextInputToFocus) {
        (nextInputToFocus as HTMLInputElement).focus();
      }
    },
    [getInputs],
  );

  const handleValidate = async (): Promise<void> => {
    const inputs = getInputs();
    const inputsValue = getInputsValue();

    setIsVerificationInProgress(true);

    await validateValue(inputsValue)
      .then((validationResult) => {
        setIsValid(validationResult);
        setAreInputsFillingByUser(false);
        setIsVerificationAvailable(false);

        if (!validationResult) {
          inputs.forEach((input) => (input.value = ''));
          inputs[0].focus();
        }
      })
      .catch(() => setIsValid(false))
      .finally(() => setIsVerificationInProgress(false));
  };

  const handleChange = (value: string): void => {
    const inputsValue = getInputsValue();

    setAreInputsFillingByUser(true);
    focusNextInput(value);
    setIsVerificationAvailable(inputsValue.length === inputQuantity && !isVerificationInProgress);
  };

  return (
    <div className="verification">
      <ConditionView if={!!label}>
        <div className="verification__label">{label}</div>
      </ConditionView>

      <div className="verification__container">
        <div className="verification__fields" style={{ gridTemplateColumns: `repeat(${inputQuantity}, 1fr)` }}>
          {new Array(inputQuantity).fill(1).map((_, index) => (
            <div
              key={`verification__field-${index + 1}`}
              className={classNames('verification__field', {
                'verification__field--invalid': isErrorShown,
                'verification__field--valid': isValid,
              })}
            >
              <VerificationInput
                disabled={isValid || disabled}
                handleChange={handleChange}
                handleKeyDown={changeFocusByKey}
              />
            </div>
          ))}
        </div>

        <ConditionView if={isVerificationAvailable}>
          <Button onClick={() => !isVerificationInProgress && handleValidate()}>
            <ConditionView if={!isVerificationInProgress} else={<Loader color="white" centered />}>
              Перевірити код
            </ConditionView>
          </Button>
        </ConditionView>

        <ConditionView if={isValid}>
          <Button>
            <Icon name="check" />
          </Button>
        </ConditionView>
      </div>

      <ConditionView if={isErrorShown}>
        <Typography color="red">
          <ConditionView if={attemptsLeft > 0}>Код не вірний. Спробуйте ще.</ConditionView>
          <ConditionView if={attemptsLeft === 0}>Кількість спроб вичерпана. Відправте нову СМС.</ConditionView>
        </Typography>
      </ConditionView>
    </div>
  );
});
